import React, { createContext, useContext, useEffect, useState } from 'react';
import { Hub } from 'aws-amplify/utils';
import {
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import { apiFetch } from '../utils/api';

interface AuthUser {
  userId: string;
  username: string;
  email?: string;
  name?: string;
  isAdmin: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = (): AuthContextValue => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async (): Promise<void> => {
    try {
      const current = await getCurrentUser();
      let attrs: Record<string, string | undefined> = {};
      try {
        attrs = await fetchUserAttributes();
      } catch {
        // attributes are best-effort
      }
      // Read group membership from the verified ID token so the UI can gate
      // the admin console client-side (the server still enforces it too).
      let groups: string[] = [];
      try {
        const session = await fetchAuthSession();
        const raw = session.tokens?.idToken?.payload['cognito:groups'];
        groups = Array.isArray(raw) ? raw.map(String) : [];
      } catch {
        // group resolution is best-effort
      }
      setUser({
        userId: current.userId,
        username: current.username,
        email: attrs.email,
        name: attrs.name,
        isAdmin: groups.includes('Admin_User'),
      });
      // Register/refresh the backend profile so the signed-in user appears in
      // the admin Users list (GET /me upserts FIS-Users from the JWT claims).
      void apiFetch('/me').catch(() => undefined);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
        case 'signInWithRedirect':
          void load();
          break;
        case 'signedOut':
          setUser(null);
          setLoading(false);
          break;
      }
    });
    void load();
    return () => unsubscribe();
  }, []);

  const signOut = async (): Promise<void> => {
    await amplifySignOut();
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, loading, signOut }}>{children}</AuthContext.Provider>;
};
